import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import isFunction from 'lodash/isFunction.js';
import { warn } from '../../utils/console.js';

function generateId() {
  return Math.random().toString(36).substr(2, 10);
}
function isObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}
function isEmpty(value) {
  if (value === 0) {
    return false;
  }
  if (isObject(value)) {
    return !Object.keys(value).length;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return !value;
}
function not(fn) {
  return function () {
    return !fn(...arguments);
  };
}
function includes(str, query) {
  if (str === undefined) {
    str = 'undefined';
  }
  if (str === null) {
    str = 'null';
  }
  if (str === false) {
    str = 'false';
  }
  const text = str.toString().toLowerCase();
  return text.includes(query.trim());
}
function flow() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }
  return x => fns.reduce((v, fn) => fn(v), x);
}
function filterOptions(options, search, labelKey, formatOptionLabel) {
  return options.filter(option => includes(formatOptionLabel(option, labelKey), search));
}
function createTaggableOption(_ref) {
  let {
    search,
    hasGroups,
    groupOptionsKey,
    groupLabelKey,
    valueKey
  } = _ref;
  const option = {
    isTag: true,
    label: search,
    type: 'option'
  };
  if (hasGroups) {
    return {
      [groupLabelKey]: 'pending-tag-group',
      [valueKey]: `tag-${generateId()}`,
      [groupOptionsKey]: [option]
    };
  }
  return option;
}
function flattenOptions(groupOptionsKey, groupLabelKey, valueKey, groupSelect) {
  return options => options.reduce((prev, curr) => {
    if (curr[groupOptionsKey] && curr[groupOptionsKey].length) {
      const groupOptions = curr[groupOptionsKey].map(option => _objectSpread2({
        type: 'option'
      }, option));
      let groupValueKey = curr[valueKey];
      const legacyGroupType = ['default', 'divider'].filter(label => [curr[groupLabelKey], groupValueKey].includes(label))[0];
      const tempTagGroupType = curr[groupLabelKey] === 'pending-tag-group';
      if (!groupValueKey && !legacyGroupType) {
        groupValueKey = curr[groupLabelKey].toLowerCase();
        warn(`Group for '${curr[groupLabelKey]}' does not contain a key matching the supplied 'valueKey' of '${valueKey}'. Make sure the group object has a '${valueKey}' key in it. Failure to include this key can affect rendering of the options list.`, this);
      }
      if (tempTagGroupType || legacyGroupType) {
        if (legacyGroupType === 'divider' && prev.length && groupOptions[0]) {
          groupOptions[0].divided = true;
        }
        return prev.concat(groupOptions);
      }
      prev.push(_objectSpread2(_objectSpread2({
        label: curr[groupLabelKey],
        [valueKey]: groupValueKey,
        disabled: !groupSelect
      }, curr), {}, {
        type: 'group',
        [groupLabelKey]: curr[groupLabelKey],
        [groupOptionsKey]: groupOptions
      }));
      return prev.concat(curr[groupOptionsKey]);
    }
    return prev;
  }, []);
}
function filterGroups(search, labelKey, groupOptionsKey, groupLabelKey, formatOptionLabel) {
  return groups => groups.map(group => {
    const groupOptions = filterOptions(group[groupOptionsKey], search, labelKey, formatOptionLabel);
    return groupOptions.length ? _objectSpread2(_objectSpread2({}, group), {}, {
      [groupLabelKey]: group[groupLabelKey],
      [groupOptionsKey]: groupOptions
    }) : [];
  });
}
function stripGroups(options) {
  return options.filter(option => option.type !== 'group');
}
function flatAndStrip(groupOptionsKey, groupLabelKey, valueKey, options) {
  return flow(flattenOptions(groupOptionsKey, groupLabelKey, valueKey), stripGroups)(options);
}
function getNextNonDisabledIndex(moveAmount, baseIndex, options, groupSelect, circular) {
  const optionCount = options.length;
  const current = options[baseIndex];
  if (!current || !current.disabled) {
    return baseIndex;
  }
  if (moveAmount > 0) {
    for (let index = baseIndex + 1; index < optionCount; index++) {
      if (!options[index].disabled) {
        return index;
      }
    }
  } else {
    for (let index = baseIndex - 1; index >= 0; index--) {
      if (!options[index].disabled) {
        return index;
      }
    }
  }
  if (circular) {
    return moveAmount > 0 ? getNextNonDisabledIndex(1, 0, options) : getNextNonDisabledIndex(-1, optionCount - 1, options);
  }
  return -1;
}
function getNextWrappingIndex(moveAmount, baseIndex, options, groupSelect) {
  let circular = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
  const optionCount = options.length;
  const optionsLastIndex = optionCount - 1;
  if (typeof baseIndex !== 'number' || baseIndex < 0 || baseIndex >= optionCount) {
    baseIndex = moveAmount > 0 ? -1 : optionsLastIndex + 1;
  }
  let newIndex = baseIndex + moveAmount;
  if (newIndex < 0) {
    newIndex = circular ? optionsLastIndex : 0;
  } else if (newIndex > optionsLastIndex) {
    newIndex = circular ? 0 : optionsLastIndex;
  }
  const nonDisabledNewIndex = getNextNonDisabledIndex(moveAmount, newIndex, options, groupSelect, circular);
  if (nonDisabledNewIndex === -1) {
    return baseIndex;
  }
  return nonDisabledNewIndex;
}
const TRIGGER_SIZE_MAP = {
  medium: 36,
  small: 32,
  mini: 26
};
function getLongestOptionLabels(arr) {
  return arr.sort((a, b) => b.label.length - a.label.length).slice(0, 10);
}
function calculateMinMenuWidthFromLabels(options) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const optionFont = '400 14px "Inter", Helvetica Neue, Arial, sans-serif';
  const groupFont = '400 12px "Inter", Helvetica Neue, Arial, sans-serif';
  const longestOptionLabels = getLongestOptionLabels(options);
  const widths = [];
  longestOptionLabels.forEach(_ref2 => {
    let {
      type,
      label
    } = _ref2;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let text = label;
    if (type === 'group') {
      ctx.font = groupFont;
      text = label.toUpperCase();
    } else {
      ctx.font = optionFont;
    }
    const {
      width
    } = ctx.measureText(text);
    widths.push(type === 'group' ? width * 1.3 : width);
  });
  return Math.max(...widths);
}
const collator = new Intl.Collator('en', {
  numeric: true,
  sensitivity: 'base'
});
function compareBooleans(a, b) {
  if (a === b) {
    return 0;
  }
  return a ? -1 : 1;
}
function sortData(a, b, vm) {
  let aValue;
  let bValue;
  if (vm.sortBy) {
    if (isFunction(vm.sortBy)) {
      aValue = vm.sortBy(a);
      bValue = vm.sortBy(b);
    } else {
      aValue = a[vm.sortBy];
      bValue = b[vm.sortBy];
    }
  } else if (vm.formatOptionLabel) {
    aValue = vm.formatOptionLabel(a, vm.labelKey);
    bValue = vm.formatOptionLabel(b, vm.labelKey);
  } else {
    aValue = a[vm.labelKey];
    bValue = b[vm.labelKey];
  }
  let result = 0;
  if (Boolean(aValue) === aValue && Boolean(bValue) === bValue) {
    result = compareBooleans(aValue, bValue);
  } else {
    result = collator.compare(aValue, bValue);
  }
  return result;
}

export { TRIGGER_SIZE_MAP, calculateMinMenuWidthFromLabels, createTaggableOption, filterGroups, filterOptions, flatAndStrip, flattenOptions, flow, generateId, getLongestOptionLabels, getNextNonDisabledIndex, getNextWrappingIndex, includes, isEmpty, isObject, not, sortData, stripGroups };
//# sourceMappingURL=multiselect-helper.js.map
